import { createRouter, createWebHistory, useRoute } from 'vue-router';
import { useAuthStore } from '../stores/authStore';
import companiesRoutes from './companies';
import authRoutes from './auth';
import threatsRoutes from './threats';
import assetsRoutes from './assets';
import usersRoutes from './users';
import rolesRoutes from './roles';
import impactsRoutes from './impacts';
import risks from './risks';
import probabilityMeasureRoutes from './probabilityMeasureTypes';
import scopesRoutes from './scopes';
import vulnerabilitiesRoutes from './vulnerabilities';
import parametersRoutes from './parameters';
import settingsRoutes from './settings';
import pentestingRoutes from './pentesting';
import mspiRoutes from './mspi';
import plansRoutes from './plans';
import addonsRoutes from './addons';
import subscriptionsRoutes from './subscriptions';
import hardCapsRoutes from './hardcaps';
import quotaRoutes from './quota';
import mySubscriptionRoutes from './mySubscription';
import dashboardRoutes from './dashboard';

import { useModuleStore } from '@/stores/moduleStore';
import { usePlanCapabilitiesStore } from '@/stores/planCapabilitiesStore';
import { isMSPIRoute } from '@/utils/utils';
import { userCanAccess } from '@/utils/authFunctions';

const PLAN_MODULE_BY_UI = {
    risksManagement: 'risk_management',
    pentesting: 'pentesting',
};

const routes = [

    {
        path: '/',
        name: 'LandingPage',
        component: () => import('../views/auth/LoginComponent.vue'),
        meta: { module: 'index' }

    },

    {
        path: '/home',
        name: 'HomePage',
        component: () => import('../views/HomePage.vue'),
    },

    {
        path: '/select-company',
        name: 'SelectCompanyPage',
        component: () => import('../views/auth/SelectCompanyComponent.vue'),

    },

    {
        path: '/home/risks',
        name: 'RiskManagementIndex',
        component: () => import('../views/RiskManagementIndex.vue'),
        meta: { module: 'risksManagement' }
    },

    {
        path: '/home/pentesting',
        name: 'PentestingIndex',
        component: () => import('../views/PentestingIndex.vue'),
        meta: { module: 'pentesting' }
    },

    {
        path: '/403',
        name: 'InsufficientPermissionsPage',
        component: () => import('../views/InsufficientPermissionsPage.vue'),
    },

    {
        path: '/:pathMatch(.*)*',
        name: 'NotFoundPage',
        component: () => import('../views/errors/NotFoundPage.vue'),
    },

    ...companiesRoutes,
    ...dashboardRoutes,
    ...authRoutes,
    ...threatsRoutes,
    ...assetsRoutes,
    ...usersRoutes,
    ...rolesRoutes,
    ...probabilityMeasureRoutes,
    ...scopesRoutes,
    ...vulnerabilitiesRoutes,
    ...impactsRoutes,
    ...risks,
    ...parametersRoutes,
    ...settingsRoutes,
    ...pentestingRoutes,
    ...mspiRoutes,
    ...plansRoutes,
    ...addonsRoutes,
    ...subscriptionsRoutes,
    ...hardCapsRoutes,
    ...quotaRoutes,
    ...mySubscriptionRoutes,

];

const router = createRouter({

    history: createWebHistory(process.env.BASE_URL),
    routes

});

router.beforeEach((to, from, next) => {

    const moduleStore = useModuleStore();
    const authStore = useAuthStore();

    if (to.meta.module) {

        moduleStore.setModule(to.meta.module);

    }

    const requiredModule = to.meta.planModule || PLAN_MODULE_BY_UI[to.meta.module];

    if (requiredModule) {

        const isSuperadminRoute = Array.isArray(to.meta.roles) && to.meta.roles.includes('SUPERADMIN');

        if (authStore.isSuperAdmin) {

            if (!isSuperadminRoute) {

                return next({ name: 'SuperAdminDashboardPage' });

            }

        } else {

            const planCapabilities = usePlanCapabilitiesStore();

            if (planCapabilities.loaded && planCapabilities.modules.length > 0 && !planCapabilities.hasModule(requiredModule)) {

                return next({ name: 'InsufficientPermissionsPage' });

            }

        }

    }

    if (to.meta.planFeature && !authStore.isSuperAdmin) {

        const planCapabilities = usePlanCapabilitiesStore();

        if (planCapabilities.loaded && planCapabilities.modules.length > 0 && !planCapabilities.hasFeature(to.meta.planFeature)) {

            return next({ name: 'InsufficientPermissionsPage' });

        }

    }

    if (to.meta.requiresAuth) {

        if (!authStore.accessToken && !authStore.authenticated) {

            window.location.href = process.env.VUE_APP_FRONTEND_URL;
            return;
        }

        if (!userCanAccess(to.meta)) {

            return next({ name: 'InsufficientPermissionsPage' });

        }

    }

    next();
});

export default router;